import CustomFieldHOC from '@common/components/CustomFieldHOC';
import { ProductAddToCartHOCRenderArgs } from '@common/components/ProductAddToCartHOC';
import ProductOptionsHOC from '@common/components/ProductOptionsHOC';
import useOnScreen from '@common/hooks/useOnScreen';
import { ReactComponent as ShipIcon } from '@common/icons/ShipIcon.svg';
import Product, { PRODUCT_TYPE } from '@common/models/Product';
import { STORE_TYPE } from '@common/models/StoreInfo';
import { addProductToCart, subtractProductFromCart } from '@common/store/cart/slice';
import { addToast } from '@common/store/toasts/slice';
import { smoothScroll } from '@common/utils';
import history from '@common/utils/history';
import SelectVariants from '@components/SelectVariants';
import ArrowDown from '@images/ArrowDown.svg';
import { ReactComponent as ArrowSmall } from '@images/ArrowSmall.svg';
import { ReactComponent as Diamond } from '@images/Diamond.svg';
import Rupee from '@instamojo/rupee';
import Image from '@lib/Image';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';

import FormCheckboxRadioGroup from '../../../../lib-components/FormCheckboxRadioGroup';
import FormInput1 from '../../../../lib-components/FormInput1';

interface ProductAddToCart {
  product: Product;
  cartProductQuantity: number;
  addToast: typeof addToast;
  addProductToCart: typeof addProductToCart;
  hocArgs: ProductAddToCartHOCRenderArgs;
  subtractProductFromCart: typeof subtractProductFromCart;
  storeType: string;
  quickView?: boolean;
  wishListFeature: boolean;
}

const ProductAddToCart: React.FC<ProductAddToCart> = ({ product, quickView, hocArgs, storeType }) => {
  const {
    finalProduct,
    editedPrice,
    setEditedPrice,
    editedPriceError,
    addToCart,
    variant,
    eventStartDate,
    eventStartTime,
    eventEndDate,
    eventEndTime,
    isBuyNowButtonDisabled,
    isAddToCartButtonDisabled,
    showAddToCartButtonAlt,
    discountCode,
    showTicketsLeft,
    finalPrice,
    savings,
  } = hocArgs;

  const { selectedOptions, setSelectedOptions, quantity, setQuantity } = variant;
  const cartCtaRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(cartCtaRef);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, seterror] = useState(false);

  return (
    <>
      <div
        className={classnames('', {
          'mb-24': quickView,
          'mb-32': !quickView,
        })}
      >
        <div className="flex justify-center md:justify-start items-center">
          <CostBar className="mr-16" finalPrice={finalPrice} savings={savings} finalProduct={finalProduct} />
          <DiscountBar className="hidden md:block" finalProduct={finalProduct} />
        </div>
        <p className="text-pricing-slashed text-10 md:text-12">Incl. of all taxes</p>
      </div>
      {finalProduct.type === PRODUCT_TYPE.EVENT ? (
        <div className="w-full md:w-auto" font-customisation="para-text">
          <div className="mb-24">
            <div className="text-heading  text-14 mb-8" color-customisation="page-body-text">
              DATE &amp; TIME
            </div>
            <div className="text-body  text-14 uppercase">
              {eventStartDate === eventEndDate ? (
                <>
                  <div>{eventStartDate},</div>
                  <div>
                    {eventStartTime} - {eventEndTime}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {eventStartDate}&nbsp;&nbsp;{eventStartTime} - {eventEndDate}&nbsp;&nbsp;{eventEndTime}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="mb-24">
            <div className="text-heading  text-14 mb-8" color-customisation="page-body-text">
              VENUE
            </div>
            <div className="text-body  text-14 uppercase">{finalProduct.event?.venue}</div>
          </div>
          {showTicketsLeft ? (
            <div className=" text-14 text-input-error mt-22">Only {finalProduct.stock} tickets left</div>
          ) : null}
        </div>
      ) : null}
      {finalProduct.payWhatYouWant ? (
        <FormInput1
          name={'price'}
          className="text-heading  text-14 mb-8"
          label={`Pay what you want (Min ₹${finalPrice})`}
          type="text"
          value={editedPrice}
          onChange={(e) => setEditedPrice(e.target.value)}
          onBlur={() => console.log('NoOp')}
          error={editedPriceError}
        />
      ) : null}
      {product?.hasVariants ? (
        <SelectVariants
          quickView={quickView}
          className={classnames('', {
            'mb-12 justify-center': !quickView,
            'py-24 my-24 border-t border-b border-input-border products-varinats': quickView,
          })}
          product={product}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          quantity={quantity}
          setQuantity={setQuantity}
        />
      ) : null}
      {product?.options?.length > 0 ? (
        <ProductOptionsHOC
          product={product}
          addToCartHocArgs={hocArgs}
          renderFormField={({ form, formInputName, charges, productOption, multiple, typeOfInput }) => {
            const { values, errors, touched, submissions, handleChange, handleBlur, asyncErrors } = form;
            return (
              <div className="mb-24">
                <div
                  className="flex mb-8 text-heading  text-14"
                  font-customisation="section-heading"
                  color-customisation="page-heading"
                >
                  <p className="max-w-3/4 break-word-only text-left">
                    {productOption?.name}
                    {productOption?.required ? <span className="text-error">*</span> : null}
                  </p>
                  <p className="flex-1 text-right ml-8 text-right pr-12">{charges}</p>
                </div>
                <FormInput1
                  name={formInputName}
                  className="text-heading text-left  text-14"
                  type={typeOfInput}
                  multiple={multiple}
                  value={values[formInputName] || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    (submissions || touched[formInputName]) && (errors[formInputName] || asyncErrors[formInputName])
                  }
                />
              </div>
            );
          }}
          renderFormCheckboxRadioField={({ form, formInputName, optionType, optionValues, charges, productOption }) => {
            const { values, errors, touched, submissions, handleChange, handleBlur, asyncErrors } = form;
            return (
              <fieldset className="mb-24 border border-input-border p-12 text-left">
                <div
                  className="flex mb-8 text-heading  text-14"
                  font-customisation="section-heading"
                  color-customisation="page-heading"
                >
                  <div className="max-w-3/4 break-word-only">
                    {productOption.name}
                    {productOption?.required ? <span className="text-error">*</span> : null}
                  </div>
                  <div className="flex-1 text-right ml-8">{charges}</div>
                </div>
                <FormCheckboxRadioGroup
                  inputClass="text-14"
                  className="text-body text-16 -ml-12"
                  name={formInputName}
                  type={optionType as 'radio' | 'checkbox'}
                  value={values[formInputName] || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={optionValues.map((optionValue) => ({ id: optionValue.id, value: optionValue.text }))}
                  error={
                    (submissions || touched[formInputName]) && (errors[formInputName] || asyncErrors[formInputName])
                  }
                  optionsActiveClass="text-theme font-semibold"
                />
              </fieldset>
            );
          }}
        />
      ) : null}
      {product?.customFields?.length > 0 ? (
        <div
          className={classnames('', {
            'border-t border-input-border pt-24 custom-fields': quickView,
          })}
        >
          <CustomFieldHOC
            isCharges={true}
            product={product}
            addToCartHocArgs={hocArgs}
            renderFormField={({ customFieldForm, formInputName, charges, productOption, multiple, typeOfInput }) => {
              const { values, errors, touched, submissions, handleChange, handleBlur, asyncErrors } = customFieldForm;
              if (values[formInputName]) seterror(false);
              else if (!values[formInputName]) seterror(true);
              return (
                <div className="mb-24">
                  <div
                    className="flex mb-8 text-heading  text-14 "
                    font-customisation="section-heading"
                    color-customisation="page-heading"
                  >
                    <p className="max-w-3/4 break-word-only text-left">
                      {productOption?.name}
                      {productOption?.required ? <span className="text-error"> *</span> : null}
                    </p>
                    <p className="flex-1 text-right ml-8 text-right pr-12">{charges}</p>
                  </div>
                  <FormInput1
                    name={formInputName}
                    className="text-heading text-left  text-14 "
                    type={typeOfInput === 'multifile' ? 'file' : typeOfInput}
                    multiple={multiple}
                    value={values[formInputName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={200}
                    error={
                      (submissions || touched[formInputName]) && (errors[formInputName] || asyncErrors[formInputName])
                    }
                  />
                </div>
              );
            }}
            renderFormCheckboxRadioField={({
              customFieldForm,
              formInputName,
              optionType,
              optionValues,
              charges,
              productOption,
            }) => {
              const { values, errors, touched, submissions, handleChange, handleBlur, asyncErrors } = customFieldForm;
              if (values[formInputName]) seterror(false);
              else if (!values[formInputName]) seterror(true);
              return (
                <fieldset className="mb-24 border border-input-border p-12 text-left">
                  <div
                    className="flex mb-12 text-heading  text-14"
                    font-customisation="section-heading"
                    color-customisation="page-heading"
                  >
                    <div className="max-w-3/4 break-word-only">
                      {productOption.name}
                      {productOption?.required ? <span className="text-error"> *</span> : null}
                    </div>
                    <div className="flex-1 text-right ml-8">{charges}</div>
                  </div>
                  <FormCheckboxRadioGroup
                    inputClass="text-14"
                    className="text-body text-16 -ml-12"
                    name={formInputName}
                    type={optionType as 'radio' | 'checkbox'}
                    value={values[formInputName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={optionValues.map((optionValue) => ({ id: optionValue.id, value: optionValue.text }))}
                    error={
                      (submissions || touched[formInputName]) && (errors[formInputName] || asyncErrors[formInputName])
                    }
                  />
                </fieldset>
              );
            }}
          />
        </div>
      ) : null}
      {product.type !== PRODUCT_TYPE.DIGITAL ? (
        finalProduct.minQuantity > 1 ? (
          <div className="text-14 text-theme mt-12" font-customisation="para-text" color-customisation="page-body-text">
            Min Order Qty: {finalProduct.minQuantity}
          </div>
        ) : null
      ) : null}
      {product.type !== PRODUCT_TYPE.DIGITAL ? (
        <QuantityOptionsBar product={finalProduct} {...{ quantity, setQuantity, quickView }} />
      ) : null}
      <div
        ref={ctaRef}
        className="flex flex-col md:flex-row justify-start w-full items-start mb-10 md:mt-auto pb-16 md:pb-0"
      >
        {showAddToCartButtonAlt && (
          <>
            <div ref={cartCtaRef} className="md:flex-1 md:min-w-fit md:max-w-302 flex flex-col items-center w-full">
              <BuyNowButton
                className="min-h-48 mb-12 md:mb-0 min-w-fit w-full"
                {...{ product, isBuyNowButtonDisabled, quickView, addToCart, discountCode, storeType }}
              />
              {product.type === PRODUCT_TYPE.PHYSICAL ? (
                <div className="hidden md:flex items-center mt-6 mx-auto  text-13 text-shipIn-text">
                  <ShipIcon className="inline-block" />
                  <div className="ml-6" font-customisation="para-text" color-customisation="page-body-text">
                    ships in {finalProduct.shipInDays} days
                  </div>
                </div>
              ) : null}
            </div>
            {!quickView ? (
              <button
                className="w-full min-w-fit md:flex-1 md:max-w-160 min-h-48 md:ml-12 p-6 text-16 bg-white text-heading border border-heading focus:outline-none"
                disabled={isAddToCartButtonDisabled}
                onClick={() => {
                  addToCart({ isBuyNowFlow: false });
                  smoothScroll(0, 0);
                }}
                font-customisation="para-text"
                button-customisation="addToCart-button"
                id="add-to-cart-btn"
              >
                Add to Bag
              </button>
            ) : (
              <button
                className={classnames(
                  `p-6 bb text-16 bg-white border border-body w-full md:w-1/2 md:ml-12 text-body focus:outline-none min-h-48`,
                  {},
                )}
                font-customisation="para-text"
                onClick={(e) => {
                  e.preventDefault();
                  history.push(product.storeLink);
                }}
                button-customisation="page-button"
              >
                View Details
              </button>
            )}
          </>
        )}
        {!showAddToCartButtonAlt && (
          <div ref={cartCtaRef}>
            <BuyNowButton
              className="min-h-48 mb-12 md:mb-0 min-w-fit w-full md:w-302 md:max-w-302"
              {...{ product, isBuyNowButtonDisabled, quickView, addToCart, discountCode, storeType }}
            />
          </div>
        )}
      </div>
      {!isOnScreen ? (
        <div className="md:hidden fixed bottom-0 left-0 right-0 py-24 px-16 bg-opacity-90 z-99999 bg-white text-16 shadow-cartCta">
          <button
            onClick={() => ctaRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'center' })}
            className="bg-theme text-white py-12 px-24 w-full"
          >
            Buy This Product
          </button>
        </div>
      ) : null}
    </>
  );
};
export default ProductAddToCart;

const BuyNowButton = ({
  product,
  isBuyNowButtonDisabled,
  quickView,
  addToCart,
  discountCode,
  storeType,
  className = '',
  // cartCtaRef,
}) => {
  return (
    <>
      {quickView ? (
        <button
          className={classnames(`p-6 text-16 bg-theme text-white focus:outline-none ${className}`, {})}
          font-customisation="para-text"
          onClick={() => {
            addToCart({ isBuyNowFlow: false });
          }}
          button-customisation="page-button"
        >
          Add to Cart
        </button>
      ) : (
        <button
          className={classnames(`group p-6 text-16 bg-theme text-white focus:outline-none ${className}`, {})}
          font-customisation="para-text"
          button-customisation="buyNow-button"
          disabled={isBuyNowButtonDisabled}
          onClick={() => addToCart({ isBuyNowFlow: true, discountLinkPromoCode: discountCode })}
          id="buy-now-btn"
        >
          <div className="w-fit mx-auto relative">
            {storeType === STORE_TYPE.ENQUIRY ? 'Enquire Now' : !product.isExpired ? 'Buy This Product' : 'Expired'}
            <ArrowSmall className="block h-full absolute opacity-0 top-0 right-0 group-hover:right-m28 group-hover:opacity-100 transition-all duration-300" />
          </div>
        </button>
      )}
    </>
  );
};

//QuantityOptionsBar

const QuantityOptionsBar = ({ quantity, setQuantity, product, quickView }) => {
  const range = (start, end, step) => {
    return Array.from(Array.from(Array(Math.ceil((end - start) / step)).keys()), (x) => start + x * step);
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  return (
    <div
      className={classnames('cursor-pointer w-full', {
        'flex justify-between items-center border-t border-input-border py-24 quantity-options-bar': quickView,
        'flex md:flex-col items-center md:items-start mb-16 pt-8': !quickView,
      })}
    >
      <div
        className={classnames('text-productCard-title text-14 leading-21 self-center md:self-start', {})}
        font-customisation="section-heading"
        color-customisation="page-heading"
      >
        Quantity
      </div>
      <div
        tabIndex={0}
        onBlur={(e) => {
          e.preventDefault();
          setIsDropdownOpen(false);
        }}
        className="focus:outline-none ml-8 md:ml-0"
      >
        <div className="flex flex-col">
          <div className="flex items-center">
            <div
              id="quantity-dropdown"
              className={classnames(
                'flex justify-between cursor-pointer pl-10 items-center flex-wrap focus:outline-none focus:h-200 border border-slideBorder',
                {
                  'w-100 min-w-100 h-44 md:h-36': !quickView,
                  'w-100 min-w-100 h-36': quickView,
                },
              )}
              onClick={(e) => {
                e.preventDefault();
                isDropdownOpen ? setIsDropdownOpen(false) : setIsDropdownOpen(true);
              }}
            >
              <span
                className="leading-21 ml-4 text-14 text-body"
                font-customisation="para-text"
                color-customisation="page-body-text"
              >
                {quantity === -1 ? 'Choose Quantity' : quantity}
              </span>
              <div className="cursor-pointer mr-16">
                <Image
                  className={'w-24 h-24 object-contain rounded bg-white'}
                  src={ArrowDown}
                  noDefaultDimensions
                  alt="Down"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          {isDropdownOpen && (
            <div
              className={classnames(
                'flex cursor-pointer absolute flex-col mt-4 border overflow-y-scroll border-variant-border bg-white z-40',
                {
                  'w-100 min-w-100 max-h-100': true,
                },
              )}
              onMouseLeave={(e) => {
                e.preventDefault();
                setIsDropdownOpen(false);
              }}
              onBlur={(e) => {
                e.preventDefault();
              }}
              font-customisation="para-text"
            >
              {range(product.minQuantity, product.maxQuantity + 1, 1).map((optionValue) => (
                <div
                  className="flex text-productCard-title text-16 leading-22  pl-10 min-h-40 items-center"
                  key={optionValue}
                  onClick={() => {
                    setQuantity(optionValue);
                    setIsDropdownOpen(false);
                  }}
                  font-customisation="para-text"
                  color-customisation="page-body-text"
                >
                  {optionValue}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CostBar = ({ finalPrice, savings, className }) => {
  return (
    <div className={`${className}`} font-customisation="para-text">
      <div className="flex items-center">
        <div
          className="text-heading text-20 md:text-22 mr-6 font-semibold md:leading-snug"
          color-customisation="page-body-text"
          id="final-price"
        >
          <Rupee removeSpaceAfterRupee value={finalPrice} />
        </div>
        {savings ? (
          <div className="flex text-pricing-slashed line-through  text-14 md:text-16" id="original-price">
            <Rupee removeSpaceAfterRupee value={savings.price} />
          </div>
        ) : (
          <div className="font-normal text-12">&nbsp;</div>
        )}
      </div>
    </div>
  );
};
const DiscountBar = ({ finalProduct, className = '' }) => {
  return finalProduct?.hasDiscount ? (
    <div className={className}>
      <div className="px-12 py-6 bg-savings bg-opacity-10" font-customisation="para-text">
        <div className="flex justify-center items-center text-13 text-savings">
          <p className="mr-6">{finalProduct?.savings?.percentage}% Off</p>
          <Diamond className="w-5 h-5 mr-6" />
          <div>
            <span>Saving </span>
            <Rupee removeSpaceAfterRupee value={finalProduct?.savings?.amount} />
          </div>
        </div>
      </div>
    </div>
  ) : null;
};
